import React, { useState, useEffect, useMemo } from 'react';
import { useTodoContext } from '../context/TodoContext';
import { useCategoryContext } from '../context/CategoryContext';
import TodoItem from './TodoItem';
import { parseISO, isPast, isToday } from 'date-fns';

const TodoList = () => {
  const { todos, loading } = useTodoContext();
  const { categories } = useCategoryContext();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCompleted, setFilterCompleted] = useState('all');
  const [filterDueDate, setFilterDueDate] = useState('all'); // nuovo filtro
  const [filteredCount, setFilteredCount] = useState(0);

  // Funzione di ordinamento task
  const sortTasks = (a, b) => {
    // Prima ordina per completamento
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }

    // Poi per data di scadenza
    if (a.dueDate && b.dueDate) {
      return parseISO(a.dueDate).getTime() - parseISO(b.dueDate).getTime();
    } else if (a.dueDate) {
      return -1;
    } else if (b.dueDate) {
      return 1;
    }

    // Infine per priorità
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  };

  // Filtra e ordina le attività
  const filteredAndSortedTodos = useMemo(() => {
    return todos.filter(todo => {
      const matchesSearch = todo.text.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = !filterPriority || todo.priority === filterPriority;
      
      // Filtro categorie
      let matchesCategory = true;
      if (filterCategory) {
        matchesCategory = Array.isArray(todo.categories) && 
          todo.categories.some(cat => {
            const catId = typeof cat === 'string' ? cat : cat._id;
            return catId === filterCategory;
          });
      }
      
      // Filtro stato completamento
      const matchesCompleted = filterCompleted === 'all' || 
                             (filterCompleted === 'completed' && todo.completed) || 
                             (filterCompleted === 'active' && !todo.completed);

      // Filtro data di scadenza
      let matchesDueDate = true;
      if (filterDueDate !== 'all' && todo.dueDate) {
        const dueDate = parseISO(todo.dueDate);
        switch (filterDueDate) {
          case 'overdue':
            matchesDueDate = isPast(dueDate) && !isToday(dueDate);
            break;
          case 'today':
            matchesDueDate = isToday(dueDate);
            break;
          case 'future':
            matchesDueDate = !isPast(dueDate) && !isToday(dueDate);
            break;
          default:
            matchesDueDate = true;
        }
      }
      
      return matchesSearch && matchesPriority && matchesCategory && matchesCompleted && matchesDueDate;
    }).sort(sortTasks);
  }, [todos, searchTerm, filterPriority, filterCategory, filterCompleted, filterDueDate]);

  useEffect(() => {
    setFilteredCount(filteredAndSortedTodos.length);
  }, [filteredAndSortedTodos]);

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat._id === categoryId);
    return category ? category.name : '';
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(cat => cat._id === categoryId);
    return category ? category.color : '#3b82f6';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterPriority('');
    setFilterCategory('');
    setFilterCompleted('all');
    setFilterDueDate('all');
  };

  if (loading) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-md shadow">
        <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Filtra Attività</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
          <div>
            <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cerca
            </label>
            <input
              type="text"
              id="searchTerm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca attività..."
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label htmlFor="filterPriority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priorità
            </label>
            <select
              id="filterPriority"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tutte le Priorità</option>
              <option value="low">Bassa</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="filterCategory" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Categoria
            </label>
            <select
              id="filterCategory"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tutte le Categorie</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="filterCompleted" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stato
            </label>
            <select
              id="filterCompleted"
              value={filterCompleted}
              onChange={(e) => setFilterCompleted(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tutte le Attività</option>
              <option value="active">Attive</option>
              <option value="completed">Completate</option>
            </select>
          </div>

          <div>
            <label htmlFor="filterDueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Scadenza
            </label>
            <select
              id="filterDueDate"
              value={filterDueDate}
              onChange={(e) => setFilterDueDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tutte le Date</option>
              <option value="overdue">Scadute</option>
              <option value="today">Oggi</option>
              <option value="future">Future</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={clearFilters}
            className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 rounded-md text-sm"
          >
            Cancella Filtri
          </button>
        </div>
      </div>
      
      {filteredAndSortedTodos.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {todos.length > 0 
            ? 'Nessuna attività corrisponde ai filtri'
            : 'Nessuna attività presente. Aggiungi qualche attività sopra!'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAndSortedTodos.map(todo => (
            <TodoItem 
              key={todo._id} 
              todo={todo} 
              getCategoryName={getCategoryName}
              getCategoryColor={getCategoryColor}
            />
          ))}
          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Mostrate {filteredAndSortedTodos.length} di {todos.length} attività
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoList;